import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { CatalogElement } from "../../../models/CatalogElement";
import { useElementsOptionsFunctions } from "../hooks/useElementsOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { ElementModal } from "./ElementModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  elementId: string | undefined;
  elements: CatalogElement[] | null;
  isLoading: boolean;
};

export const TableElement = ({ elementId, elements, isLoading }: Props) => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [element, setElement] = useState<CatalogElement | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [quickFilterText, setQuickFilterText] = useState("");

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewElement = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setElement(null);
    navigate(`/configuration/lab-elements/new`);
  };

  const { getMenuOptions } = useElementsOptionsFunctions({
    setElement,
    setShowModal,
    handleNewElement,
  });
  const { rows, columnDefs } = useTableData({
    elements,
    setElement,
    setShowModal,
    showContextMenu,
  });
  const { columnDefaults } = useGridDefinitions({
    OnEdit: (data: CatalogElement) => {
      setShowModal("edit");
      setElement(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setElement(event.data as CatalogElement);
      setShowModal("edit");
      navigate(
        `/configuration/lab-elements/${
          (event.data as CatalogElement).ElementId
        }`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/lab-elements/${
          (event.data as CatalogElement).ElementId
        }`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "elementContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setElement(null);
    navigate(`/configuration/lab-elements/`);
  };

  useEffect(() => {
    if (elementId) {
      if (elementId === "new") {
        handleNewElement();
      } else {
        let partNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogElement).ElementId === Number(elementId)) {
            partNode = n;
            return true;
          }
        });

        if (partNode!) {
          gridApi?.ensureIndexVisible(partNode.rowIndex ?? 0, "middle");
          partNode.setSelected(true);
          setElement(partNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "elementContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Elements
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ padding: "8px 15px" }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search elements..."
            value={quickFilterText}
            onChange={(e) => setQuickFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            style={{ width: 280 }}
          />
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogElement>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={
                {
                  ...columnDefaults,
                  filter: false,
                  floatingFilter: false,
                } as ColDef<CatalogElement>
              }
              quickFilterText={quickFilterText}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              loading={isLoading}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.ElementId)}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="ELEMENT" onClick={handleNewElement} />
        </Grid>
      </Grid>
      <ElementModal
        element={element}
        elements={elements}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
      />
    </>
  );
};
